import React, { useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Edit, Trash2 } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { KnownQuestion, CustomQuestion } from "./CallScriptManager";
import QuestionForm from "./QuestionForm";

const CATEGORY_ORDER = [
  "PlanInformation",
  "Maximums",
  "ClaimsInformation",
  "BenefitsDetails",
  "ExtraDentalInfo",
  "ExtraMedicalInfo",
  "CallDetails",
];

interface QuestionOrderPanelProps {
  orderedQuestionIds: string[];
  knownQuestions: KnownQuestion[];
  customQuestions: CustomQuestion[];
  onReorder: (newOrder: string[]) => void;
  onUpdateCustomQuestion?: (questionId: string, question: Omit<CustomQuestion, "id">) => void;
  onRemoveCustomQuestion?: (questionId: string) => void;
}

interface SortableQuestionItemProps {
  questionId: string;
  question: KnownQuestion | CustomQuestion;
  categoryIndex: number;
  questionIndex: number;
  onEdit?: (question: CustomQuestion) => void;
  onDelete?: (questionId: string) => void;
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  questionId,
  question,
  categoryIndex,
  questionIndex,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: questionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border rounded-lg bg-white transition-all ${
        isDragging
          ? "shadow-lg border-[#005151] z-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0"
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Question Number */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#005151]/10 flex items-center justify-center">
        <span className="text-xs font-semibold text-[#005151]">{questionIndex + 1}</span>
      </div>

      {/* Question Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <p className="text-sm font-medium text-gray-900 break-words whitespace-normal">
          {question.question_text}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {question.is_custom && (
            <Badge
              variant="default"
              className="text-xs font-normal bg-[#005151] text-white"
            >
              Custom
            </Badge>
          )}
          {question.procedure_code && (
            <Badge variant="outline" className="text-xs font-normal">
              Code: {question.procedure_code}
            </Badge>
          )}
        </div>
      </div>

      {/* Edit/Delete buttons for custom questions */}
      {question.is_custom && (onEdit || onDelete) && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(question as CustomQuestion);
              }}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(questionId);
              }}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const QuestionOrderPanel: React.FC<QuestionOrderPanelProps> = ({
  orderedQuestionIds,
  knownQuestions,
  customQuestions,
  onReorder,
  onUpdateCustomQuestion,
  onRemoveCustomQuestion,
}) => {
  const [editingQuestion, setEditingQuestion] = useState<CustomQuestion | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map of all questions for quick lookup
  const questionsMap = useMemo(() => {
    const map = new Map<string, KnownQuestion | CustomQuestion>();

    knownQuestions.forEach((q) => {
      map.set(q.id, q);
    });

    customQuestions.forEach((q) => {
      const id = q.id || "";
      if (id) {
        map.set(id, q);
      }
    });

    return map;
  }, [knownQuestions, customQuestions]);

  // Group questions by category while maintaining order
  const questionsByCategory = useMemo(() => {
    const grouped: Record<string, Array<{ id: string; question: KnownQuestion | CustomQuestion }>> = {};

    orderedQuestionIds.forEach((id) => {
      const question = questionsMap.get(id);
      if (question) {
        const category = question.category;
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push({ id, question });
      }
    });

    return grouped;
  }, [orderedQuestionIds, questionsMap]);

  // Format category name for display
  const formatCategoryName = (category: string): string => {
    return category
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Handle drag end - only allow reordering within the same category
  const handleDragEnd = (event: DragEndEvent, category: string) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Get all question IDs in this category
      const categoryQuestions = questionsByCategory[category] || [];
      const categoryQuestionIds = categoryQuestions.map((item) => item.id);

      // Find indices within this category
      const oldIndex = categoryQuestionIds.indexOf(active.id as string);
      const newIndex = categoryQuestionIds.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Reorder within category
        const reorderedCategoryIds = arrayMove(categoryQuestionIds, oldIndex, newIndex);

        // Rebuild the full ordered list maintaining category order
        const newOrder: string[] = [];
        const categoryOrder = [...CATEGORY_ORDER];

        // Add categories in order
        categoryOrder.forEach((cat) => {
          if (cat === category) {
            // Use reordered IDs for this category
            newOrder.push(...reorderedCategoryIds);
          } else if (questionsByCategory[cat]) {
            // Keep other categories in their original order
            newOrder.push(...questionsByCategory[cat].map((item) => item.id));
          }
        });

        // Add any categories not in CATEGORY_ORDER at the end
        Object.keys(questionsByCategory).forEach((cat) => {
          if (!categoryOrder.includes(cat)) {
            if (cat === category) {
              newOrder.push(...reorderedCategoryIds);
            } else {
              newOrder.push(...questionsByCategory[cat].map((item) => item.id));
            }
          }
        });

        onReorder(newOrder);
      }
    }
  };

  // Get sorted category keys - CallDetails always at the end
  const sortedCategories = useMemo(() => {
    const categories = Object.keys(questionsByCategory);
    return categories.sort((a, b) => {
      // CallDetails always goes to the end
      if (a === "CallDetails" && b !== "CallDetails") return 1;
      if (b === "CallDetails" && a !== "CallDetails") return -1;

      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [questionsByCategory]);

  return (
    <div className="space-y-4">
      {orderedQuestionIds.length === 0 ? (
        <div className="text-center py-12">
          <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <GripVertical className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No questions selected
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Select questions from the Sample Questions panel to add them here.
            Drag and drop to reorder within each category.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            // Determine which category this drag belongs to
            const activeId = event.active.id as string;
            const question = questionsMap.get(activeId);
            if (question) {
              handleDragEnd(event, question.category);
            }
          }}
        >
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {sortedCategories.map((category, categoryIndex) => {
              const categoryQuestions = questionsByCategory[category];
              const categoryQuestionIds = categoryQuestions.map((item) => item.id);

              // Calculate global starting index for this category
              let globalIndex = 0;
              for (let i = 0; i < categoryIndex; i++) {
                globalIndex += questionsByCategory[sortedCategories[i]].length;
              }

              return (
                <div key={category} className="space-y-2">
                  {/* Category Header */}
                  <div className="sticky top-0 bg-white z-10 pb-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {formatCategoryName(category)}
                      </h3>
                      <Badge variant="outline" className="text-xs">
                        {categoryQuestions.length} question{categoryQuestions.length !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                  </div>

                  {/* Questions in this category */}
                  <SortableContext
                    items={categoryQuestionIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2 pl-2">
                      {categoryQuestions.map(({ id, question }, questionIndex) => (
                        <SortableQuestionItem
                          key={id}
                          questionId={id}
                          question={question}
                          categoryIndex={categoryIndex}
                          questionIndex={globalIndex + questionIndex}
                          onEdit={question.is_custom ? (q) => {
                            setEditingQuestion(q);
                            setIsFormOpen(true);
                          } : undefined}
                          onDelete={question.is_custom && onRemoveCustomQuestion ? (questionId) => {
                            onRemoveCustomQuestion(questionId);
                          } : undefined}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              );
            })}
          </div>
        </DndContext>
      )}

      {orderedQuestionIds.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            {orderedQuestionIds.length} question
            {orderedQuestionIds.length !== 1 ? "s" : ""} in order
          </p>
          <p className="text-xs text-gray-500 text-center mt-1">
            Drag items to reorder within each category
          </p>
        </div>
      )}

      {/* Question Form Dialog for editing */}
      {onUpdateCustomQuestion && (
        <QuestionForm
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open);
            if (!open) setEditingQuestion(null);
          }}
          question={editingQuestion}
          onSave={(questionData) => {
            if (editingQuestion && onUpdateCustomQuestion) {
              // Update existing question
              const questionId = editingQuestion.id || "";
              onUpdateCustomQuestion(questionId, questionData);
            }
            setIsFormOpen(false);
            setEditingQuestion(null);
          }}
        />
      )}
    </div>
  );
};

export default QuestionOrderPanel;
